import React, { useState, useMemo, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import {
  MenuContainer,
  ProductCategoryTabs,
  Tab,
  ProductList,
  CategoryLabelContainer,
  CategoryIcon,
  CategoryLabel,
  CategorySelectContainer,
  CategorySelectTopContainer,
} from './styled';
import {
  brandCategoryProductsByStore as brandCategoryProductsByStoreQuery,
  productCategories as productCategoriesMutation,
  productCategories as productCategoriesQuery,
  brandVatRatesByStore as brandVatRatesByStoreQuery,
} from '../../../graphql/queries';
import ProductItem from './ProductItem';
import { IconButton, LabeledIconButton } from './ProductItem/styled';
import CategoryEditModal from './CategoryEditModal';
import { transformBrandCategoryData } from '@/utils/data';
import AddProductModal from './ProductItem/ProductOrderSheet/AddProductModal';
import Button from '../../../components/Button';
import {
  addCategoryToStore as addCategoryToStoreMutation,
  addProductsToCategory as addProductsToCategoryMutation,
  copyCategoriesMutation,
  createCategory as createCategoryMutation,
  ingestPlaceholderProduct as ingestPlaceholderProductMutation,
  removeCategoryFromStore as removeCategoryFromStoreMutation,
} from '../../../graphql/mutations';
import Checkbox from '../../../components/Checkbox';
import { Spacer } from './ProductItem/ProductOrderSheet/ExtraSelector/styled';
import Icon from '../../../components/Icon';
import { CopyPasteContext } from '../../../providers/CopyPasteContextProvider';
import HiddenReusableIndicator from '../../../components/HiddenReusableIndicator';
import i18n from '@/utils/i18n';

function Menu({ storeId, brandId }) {
  const [tabIndex, setTabIndex] = useState(0);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [selectedProduct, setProduct] = useState(null);
  const [editingCategory, setCategory] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [addingProductToCategory, setAddingProductToCategory] = useState(null);
  const tabBarRef = useRef();
  const sectionRefs = useRef([]).current;
  const categoryRefs = useRef([]).current;
  const { data = { productCategories: null } } = useQuery(
    productCategoriesQuery,
    { variables: { id: storeId, filter: 'ALL' }, fetchPolicy: 'network-only' },
  );
  const { data: brandCategoryProductsData } = useQuery(
    brandCategoryProductsByStoreQuery,
    { variables: { store_id: storeId }, fetchPolicy: 'network-only' },
  );
  const { data: vatRates } = useQuery(brandVatRatesByStoreQuery, {
    variables: { store_id: storeId },
    fetchPolicy: 'network-only',
  });
  const [isCreatingNewProduct, setIsCreatingNewProduct] = useState(false);
  const copyPasteContext = useContext(CopyPasteContext);
  const [ingestPlaceholderProduct] = useMutation(
    ingestPlaceholderProductMutation,
  );
  const [createCategory] = useMutation(createCategoryMutation);
  const [addCategoryToStore] = useMutation(addCategoryToStoreMutation);
  const [addProductsToCategory] = useMutation(addProductsToCategoryMutation);
  const [removeCategoryFromStore] = useMutation(
    removeCategoryFromStoreMutation,
  );
  const [copyCategories, { loading: copyingCategories }] = useMutation(
    copyCategoriesMutation,
  );

  const { productCategories } = data;

  const { productCategoryMap, allExtrasForBrand, allProductsForBrand } =
    useMemo(
      () => transformBrandCategoryData(brandCategoryProductsData),
      [brandCategoryProductsData],
    );

  function onClickCategoryTab(index) {
    categoryRefs[index].scrollIntoView({
      inline: 'center',
      block: 'nearest',
    });
    scrollToSection(sectionRefs[index]);
    setTabIndex(index);
  }

  function handleClickProduct(item) {
    const nextItem =
      selectedProduct && selectedProduct.id === item.id ? null : item;
    setProduct(nextItem);
  }

  function scrollToSection(ref) {
    if (tabBarRef.current) {
      const tabBarHeight = tabBarRef.current.clientHeight;
      window.scrollTo({
        top: ref.offsetTop - tabBarHeight,
        behavior: 'smooth',
      });
    }
  }

  function closeEditModal() {
    setCategory(null);
  }

  function removeCategory(category) {
    if (
      !window.confirm(
        'Are you sure you want to remove this category from this store?',
      )
    ) {
      return;
    }

    removeCategoryFromStore({
      variables: {
        store_id: storeId,
        category_id: category.id,
      },
      refetchQueries: [
        {
          query: productCategoriesQuery,
          variables: {
            id: storeId,
            filter: 'ALL',
          },
        },
      ],
    });
  }

  async function createAndAddNewProduct(category) {
    if (!brandId) {
      return;
    }
    setIsCreatingNewProduct(true);
    const ingestPrdRes = await ingestPlaceholderProduct({
      variables: {
        brand_id: brandId,
      },
    });

    const newProductId = ingestPrdRes.data.ingestProduct.id;
    if (!newProductId) {
      alert(i18n.t('Menu:errorAddProduct'));
      return;
    }
    await addProductsToCategory({
      variables: {
        category_id: category.id,
        product_ids: [newProductId],
      },
      refetchQueries: [
        {
          query: productCategoriesQuery,
          variables: {
            id: storeId,
            filter: 'ALL',
          },
        },
      ],
    });
    setIsCreatingNewProduct(false);
  }

  async function onCreateCategory() {
    setIsAddingCategory(true);
    const newCategory = await createCategory({
      variables: {
        new_category: {
          name: '🐇 Placeholder',
          products: [],
        },
      },
    });
    const newCategoryId = newCategory.data.createCategory.id;
    if (!newCategoryId) {
      setIsAddingCategory(false);
      alert(i18n.t('categoryError'));
      return;
    }

    // we need to add a placeholder product to the category, otherwise the category will not be visible
    const ingestPrdRes = await ingestPlaceholderProduct({
      variables: {
        brand_id: brandId,
      },
    });
    const newProductId = ingestPrdRes.data.ingestProduct.id;
    await addProductsToCategory({
      variables: {
        category_id: newCategoryId,
        product_ids: [newProductId],
      },
    });

    // add category to store
    await addCategoryToStore({
      variables: {
        store_id: storeId,
        category_id: newCategoryId,
      },
      refetchQueries: [
        {
          query: productCategoriesQuery,
          variables: {
            id: storeId,
            filter: 'ALL',
          },
        },
      ],
    });
    setIsAddingCategory(false);
  }

  const onSelectCategoryClick = React.useCallback((categoryId) => {
    setSelectedCategories((prevState) => {
      const isSelected = prevState.indexOf(categoryId) !== -1;
      if (isSelected) return prevState.filter((id) => id !== categoryId);
      return [...prevState, categoryId];
    });
  });
  const onSelectAllCategoryClick = React.useCallback(() => {
    setSelectedCategories((prevState) => {
      if (prevState.length === 0) {
        // select all
        return (productCategories || []).map((cat) => cat.id);
      }
      // select none
      return [];
    });
  }, [productCategories]);

  const onCopyClick = React.useCallback(() => {
    copyPasteContext.setCategories(selectedCategories);
    setSelectedCategories([]);
  }, [selectedCategories, setSelectedCategories, copyPasteContext]);
  const onPasteClick = React.useCallback(() => {
    copyCategories({
      variables: {
        ids: copyPasteContext.categories,
        add_to_store: storeId,
      },
      refetchQueries: [
        {
          query: productCategoriesMutation,
          variables: {
            id: storeId,
            filter: 'ALL',
          },
        },
      ],
    });
  }, [copyPasteContext.categories, storeId, copyCategories]);

  return (
    <MenuContainer>
      <ProductCategoryTabs ref={tabBarRef}>
        {(productCategories || []).map((category, index) => (
          <Tab
            key={category.id}
            title={category.id}
            ref={(ref) => {
              categoryRefs[index] = ref;
            }}
            active={tabIndex === index}
            onClick={() => onClickCategoryTab(index)}
          >
            {category.name}
          </Tab>
        ))}
      </ProductCategoryTabs>

      <ProductList>
        <CategorySelectTopContainer>
          <Checkbox
            onClick={onSelectAllCategoryClick}
            checked={
              selectedCategories.length === (productCategories || []).length
            }
          />
          <Spacer />
          <Button
            disabled={selectedCategories.length === 0}
            onClick={onCopyClick}
            small
          >
            <Icon name="clipboard" paddingTop={3} />
            {` ${i18n.t('Menu:copy')} (${selectedCategories.length})`}
          </Button>
          <Spacer />
          <Button
            disabled={copyPasteContext.categories.length === 0}
            onClick={onPasteClick}
            small
            loading={copyingCategories}
          >
            {i18n.t('Menu:paste')}
          </Button>
        </CategorySelectTopContainer>
        {(productCategories || []).map((category, index) => (
          <React.Fragment key={category.id}>
            <div
              ref={(ref) => {
                sectionRefs[index] = ref;
              }}
            />
            <CategorySelectContainer
              onClick={() => onSelectCategoryClick(category.id)}
            >
              <Checkbox
                checked={selectedCategories.indexOf(category.id) !== -1}
              />
            </CategorySelectContainer>
            <CategoryLabelContainer>
              <CategoryIcon src={category.icon} alt="" noPlaceholder />
              <CategoryLabel title={category.id}>
                {category.name}{' '}
                <HiddenReusableIndicator
                  published={category.published}
                  reusable_packaging={category.reusable_packaging}
                />
              </CategoryLabel>
              <IconButton onClick={() => setCategory(category)} name="edit" />
              <IconButton
                onClick={() => setAddingProductToCategory(category)}
                name="plus"
                title={i18n.t('Menu:addProduct')}
              />
              <IconButton
                onClick={() => removeCategory(category)}
                name="trash"
                color="red"
              />
              <Button
                small
                disabled={!brandId}
                onClick={() => createAndAddNewProduct(category)}
                loading={isCreatingNewProduct}
              >
                {i18n.t('Menu:newProduct')}
              </Button>
            </CategoryLabelContainer>
            {category.products.map((product) =>
              product ? (
                <ProductItem
                  key={product.id}
                  product={product}
                  category={category}
                  storeId={storeId}
                  brandId={brandId}
                  onClick={handleClickProduct}
                  selected={
                    selectedProduct && selectedProduct.id === product.id
                  }
                  tabBarRef={tabBarRef}
                  productCategoryMap={productCategoryMap}
                  allExtrasForBrand={allExtrasForBrand}
                  vatRates={
                    (vatRates || { brandVatRatesByStore: [] })
                      .brandVatRatesByStore
                  }
                />
              ) : (
                <h2>{i18n.t('Menu:productError')}</h2>
              ),
            )}
          </React.Fragment>
        ))}
        <CategoryLabelContainer>
          <LabeledIconButton
            name="plus"
            onClick={onCreateCategory}
            loading={isAddingCategory}
          >
            {i18n.t('Menu:addCategory')}
          </LabeledIconButton>
        </CategoryLabelContainer>
      </ProductList>

      {editingCategory && (
        <CategoryEditModal
          category={editingCategory}
          storeId={storeId}
          onClose={closeEditModal}
        />
      )}

      {addingProductToCategory && (
        <AddProductModal
          category={addingProductToCategory}
          onClose={() => setAddingProductToCategory(null)}
          allProductsForBrand={allProductsForBrand}
          storeId={storeId}
        />
      )}
    </MenuContainer>
  );
}

Menu.propTypes = {
  storeId: PropTypes.string.isRequired,
  brandId: PropTypes.string,
};

Menu.defaultProps = {
  brandId: null,
};

export default Menu;
